import { ApolloClient, InMemoryCache, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import { API_URL } from "../Utils/utils";
import Pusher from "pusher-js";
import PusherLink from "./PusherLink";
import { ApolloLink } from "@apollo/client/core";
import {
  getAuthGeneration,
  getAuthToken,
  getCsrfToken,
  redirectSuspendedAccount,
  resetCsrfToken,
} from "./tokenUtils";
import Cookies from "js-cookie";

const isLocalOnline = process.env.REACT_APP_ENV === 'local-online';

const selectedTeamUserId = () => {
  if (isLocalOnline) return undefined;
  try {
    return window.localStorage.getItem("currentTeamUser") || undefined;
  } catch (error) {
    return undefined;
  }
};

const teamUserHeader = () => {
  const teamUserId = selectedTeamUserId();
  return teamUserId ? { 'X-Team-User-Id': teamUserId } : {};
};

// Create the upload link
const uploadLink = createUploadLink({
  uri: API_URL + "/graphql",
  credentials: isLocalOnline ? 'same-origin' : 'include',
});

// Authorization logic using both cookies and localStorage
const authLink = setContext(async (_, { headers }) => {
  if (window.location !== window.parent.location) {
    // If in an iframe, skip adding authorization
    return {
      headers: {
        ...headers,
        'X-Client-Auth-Generation': getAuthGeneration(),
        ...teamUserHeader(),
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
    };
  } else {
    if (isLocalOnline) {
      // Token-based authentication
      const token = getAuthToken(); // Retrieve token from storage
      if (!token) {
        throw new Error("No authentication token found. Please login.");
      }
      return {
        headers: {
          ...headers,
          'X-Client-Auth-Generation': getAuthGeneration(),
          ...teamUserHeader(),
          Authorization: `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
        },
      };
    } else {
      // Session-based authentication
      await getCsrfToken(); // Ensure CSRF token is loaded
      const xsrfToken = Cookies.get('XSRF-TOKEN');
      return {
        headers: {
          ...headers,
          'X-Client-Auth-Generation': getAuthGeneration(),
          ...teamUserHeader(),
          'X-XSRF-TOKEN': xsrfToken ? decodeURIComponent(xsrfToken) : undefined,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
        },
      };
    }
  }
});

const pusherLink = new PusherLink({
  pusher: new Pusher(process.env.REACT_APP_PUSHER_API_KEY, {
    cluster: process.env.REACT_APP_PUSHER_CLUSTER,
    authEndpoint: `${process.env.REACT_APP_BACKEND_URL}/graphql/subscriptions/auth`,
    auth: {
      headers: isLocalOnline
        ? {
          Authorization: `Bearer ${getAuthToken()}`,
        }
        : {
          'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN') || '',
        },
    },
  }),
});

// Error handling link
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (
      graphQLErrors?.some(
        (error) => error.extensions?.code === "ACCOUNT_SUSPENDED",
      )
    ) {
      const requestGeneration = String(
        operation.getContext()?.headers?.["X-Client-Auth-Generation"] ?? "",
      );

      if (requestGeneration === getAuthGeneration()) {
        redirectSuspendedAccount();
      }
    }

  if (
      networkError &&
      (networkError.statusCode === 419 || networkError.response?.status === 419)
    ) {
      return new Observable((observer) => {
        resetCsrfToken()
          .then(() => {
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    }
  },
);

// Combine the links in the correct order
const link = ApolloLink.from([errorLink, authLink, pusherLink, uploadLink]);

// Create the Apollo Client instance
const graphqlClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
});
// const graphqlClient = new ApolloClient({
//   uri: API_URL + "/graphql",
//   cache: new InMemoryCache(),
//   headers: {
//     authorization: apiToken ? `Bearer ${apiToken}` : "",
//   },
// });
export default graphqlClient;
